import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, KeyRound, ArrowLeft, Mail } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getParentUnlock, setParentUnlock } from "@/lib/parentUnlock";

function PinInput({ value, onChange, testid, autoFocus }) {
  return (
    <input value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
      inputMode="numeric" type="password" autoComplete="off" autoFocus={autoFocus} placeholder="••••" data-testid={testid}
      className="w-full h-14 text-center text-3xl tracking-[0.6em] rounded-xl border-2 border-slate-200 focus:border-blue-500 outline-none font-bold" />
  );
}

function EmailResetLink() {
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const send = async () => {
    setState("busy");
    try {
      const { data } = await api.post("/parent-pin/email-reset");
      setState("sent");
      setMsg(`We sent a reset link to ${data.sent_to}. It works once and expires in 1 hour.`);
    } catch (err) {
      setState("idle");
      setMsg(formatError(err?.response?.data?.detail));
    }
  };
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm" data-testid="pin-email-reset">
      <p className="text-slate-600">Forgot your account password too?</p>
      <button type="button" onClick={send} disabled={state !== "idle"} data-testid="pin-email-reset-btn"
        className="mt-1.5 text-blue-600 font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
        {state === "busy" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} {state === "sent" ? "Email sent" : "Email me a reset link"}
      </button>
      {msg && <p className={`mt-1.5 text-xs ${state === "sent" ? "text-emerald-700" : "text-red-600"}`} data-testid="pin-email-reset-msg">{msg}</p>}
    </div>
  );
}

function GateForm({ mode, setMode, onUnlocked }) {
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pin.length !== 4) return setError("The PIN must be 4 digits.");
    if (mode !== "enter" && pin !== pin2) return setError("The two PINs don't match.");
    setBusy(true);
    try {
      const url = { enter: "/parent-pin/verify", create: "/parent-pin/set", reset: "/parent-pin/reset" }[mode];
      const { data } = await api.post(url, mode === "reset" ? { pin, password } : { pin });
      onUnlocked(data.parent_unlock);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
      setPin(""); setPin2("");
    } finally {
      setBusy(false);
    }
  };

  const title = { enter: "Enter your parent PIN", create: "Create a parent PIN", reset: "Reset your parent PIN" }[mode];
  const sub = {
    enter: "This area is for parents only.",
    create: "Choose a 4-digit PIN so only you can open the parent area and answer review.",
    reset: "Enter your account password, then choose a new 4-digit PIN.",
  }[mode];
  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`pin-form-${mode}`}>
      <h1 className="text-2xl font-bold" data-testid="pin-title">{title}</h1>
      <p className="text-sm text-slate-500">{sub}</p>
      {mode === "reset" && (
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Account password"
          data-testid="pin-reset-password" className="w-full h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 outline-none" />
      )}
      <PinInput value={pin} onChange={setPin} testid="pin-input" autoFocus />
      {mode !== "enter" && <PinInput value={pin2} onChange={setPin2} testid="pin-confirm-input" />}
      {mode !== "enter" && <p className="text-xs text-slate-400 text-center -mt-1">Type the PIN again to confirm</p>}
      {error && <p className="text-sm text-red-600 font-medium" data-testid="pin-error">{error}</p>}
      <button disabled={busy} data-testid="pin-submit" className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />} {mode === "enter" ? "Unlock" : "Save PIN"}
      </button>
      {mode === "enter" && (
        <button type="button" onClick={() => setMode("reset")} data-testid="pin-forgot" className="w-full text-sm text-blue-600 font-medium inline-flex items-center justify-center gap-1.5">
          <KeyRound size={14} /> Forgot PIN?
        </button>
      )}
      {mode === "reset" && <EmailResetLink />}
      {mode === "reset" && (
        <button type="button" onClick={() => setMode("enter")} data-testid="pin-back-to-enter" className="w-full text-sm text-slate-500">Back</button>
      )}
    </form>
  );
}

export default function ParentGate({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(() => Boolean(user?.is_admin || getParentUnlock()));
  const [mode, setMode] = useState(null);

  useEffect(() => {
    if (unlocked) return;
    api.get("/parent-pin/status").then(({ data }) => {
      if (data.is_admin) setUnlocked(true);
      else setMode(data.has_pin ? "enter" : "create");
    }).catch(() => setMode("enter"));
  }, [unlocked]);

  if (unlocked) return children;
  if (!mode) return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;
  return (
    <div className="min-h-screen bg-[#F8FAFC] font-parent text-slate-900 flex items-center justify-center p-5" data-testid="parent-gate">
      <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-200 shadow-sm p-7">
        <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4"><Lock size={22} /></div>
        <GateForm mode={mode} setMode={setMode} onUnlocked={(t) => { setParentUnlock(t); setUnlocked(true); }} />
        <div className="flex justify-between mt-6 pt-4 border-t border-slate-100 text-sm">
          <button onClick={() => navigate(-1)} data-testid="pin-go-back" className="text-slate-500 inline-flex items-center gap-1"><ArrowLeft size={14} /> Go back</button>
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="pin-logout" className="text-slate-500">Log out</button>
        </div>
      </div>
    </div>
  );
}
